import React, {
  FC,
  useCallback,
  useEffect,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import classNames from "classnames";
import deepClone from "../../../../helpers/deepClone";
import { truncateString } from "../../../../helpers/formatters";
import useWatchState from "../../../../hooks/useWatchState";
import Checkbox from "../../../ActionsSelectors/Checkbox/Checkbox/Checkbox";
import FilterFormLabel from "../../../Atoms/FormLabel/FilterFormLabel";
import FormCheckWrapper from "../../../Atoms/FormLabel/FormCheckWrapper";
import ScrollContainer from "../../../Utility/ScrollContainer/ScrollContainer";
import useTheme from "../../../Utility/ThemeProvider/useTheme";
import { IFilterTagData } from "../../FilterTags/FilterTag";
import SearchInput from "../../Search/SearchInput/SearchInput";
import AbstractFilter from "../AbstractFilter/AbstractFilter";
import { ICheckboxFilterData, ICheckboxFilterItem } from "../Filter.interface";
import FilterFormItemWrapper from "../FilterFormItemWrapper/FilterFormItemWrapper";
import ShowMoreLess from "../ShowMoreLess/ShowMoreLess";
import { ICheckboxFilterProps } from "./CheckboxFilter.interface";

export const resetCheckboxFilter = (
  data: ICheckboxFilterData,
): ICheckboxFilterData => {
  const newData = deepClone(data);
  newData.items = newData.items.map((item) => ({
    ...item,
    checked: false,
  }));
  return newData;
};

export const checkboxFilterItemToTagsDTO = (
  data: ICheckboxFilterData,
): IFilterTagData[] =>
  data.items
    .filter((item) => item.checked)
    .map((item) => ({
      id: item.id,
      text: item.title,
      category: data.name,
    }));

const emptyArrLink = [];

const CheckboxFilter: FC<ICheckboxFilterProps> = ({
  filterData,
  hasShowMore = true,
  showMoreItemsLimit = 5,
  opened,
  showMoreExpanded = false,
  hasSearch = false,
  searchTerm = "",
  labels,
  variant,
  classes,
  onSearch,
  onChange,
  onToggleItem,
  ...abstractFilterProps
}) => {
  const theme = useTheme(variant);
  const inputRef = useRef(null);

  const [filterItemsState, setFilterItems] = useWatchState<
    ICheckboxFilterItem[]
  >({ value: filterData.items || emptyArrLink });

  const [displayItemsState, setDisplayItems] = useWatchState<
    ICheckboxFilterItem[]
  >({
    value: filterData.items,
  });

  const [searchTermState, setSearchTerm] = useWatchState<string>({
    value: searchTerm,
  });

  const [isShowMoreExpandedState, setIsExpanded] = useWatchState<boolean>({
    value: showMoreExpanded,
  });

  const [displayShowMoreState, setDisplayShowMore] = useState<boolean>(
    hasShowMore && filterItemsState.length > showMoreItemsLimit,
  );

  const handleOnSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      onSearch?.(value);
    },
    [onSearch, setSearchTerm],
  );

  const handleCheckboxChange = useCallback(
    ({ id, value }: { id: string; value: boolean }) => {
      const updatedItems = deepClone(filterItemsState).map((item) => ({
        ...item,
        checked: item.id === id ? value : item.checked,
      }));

      setFilterItems(updatedItems);
      onChange?.({ values: updatedItems, updates: [{ fieldName: id, value }] });
    },
    [filterItemsState, onChange, setFilterItems],
  );

  const handleClear = useCallback(() => {
    const updatedItems = resetCheckboxFilter(filterData).items;
    setFilterItems(updatedItems);
    onChange?.({
      values: updatedItems,
      updates: updatedItems.map(({ id, checked }) => ({
        fieldName: id,
        value: checked,
      })),
    });
  }, [filterData, setFilterItems, onChange]);

  useLayoutEffect(() => {
    setDisplayShowMore(
      hasShowMore && filterItemsState.length > showMoreItemsLimit,
    );
  }, [filterItemsState, hasShowMore, showMoreItemsLimit]);

  useEffect(() => {
    /**
     * If SearchTerm value is not null filter Items
     */
    let newDisplayItems = searchTermState
      ? filterItemsState.filter((item) =>
          item.title.toLowerCase().includes(searchTermState.toLowerCase()),
        )
      : filterItemsState;

    /**
     * If showMore functionality is enable - slice display items array
     */
    displayShowMoreState &&
      !isShowMoreExpandedState &&
      (newDisplayItems = newDisplayItems.slice(0, showMoreItemsLimit));

    setDisplayItems(newDisplayItems);
  }, [
    displayShowMoreState,
    filterItemsState,
    isShowMoreExpandedState,
    searchTermState,
    setDisplayItems,
    showMoreItemsLimit,
  ]);

  const checkedItems = filterItemsState.filter((item) => item.checked === true);

  const selectedItemsTitles = checkedItems.map((item) => item.title).join(", ");

  const totalItemsCount = checkedItems.length;

  const noResults = hasSearch ? (
    <p
      className={classNames("text-paragraph", {
        "text-color-white": theme === "dark",
        "my-6": abstractFilterProps.filterType === "accordion",
        "px-4 mb-4": abstractFilterProps.filterType !== "accordion",
      })}
      data-testid="no-search-results"
    >
      {labels?.noResultsMessage}
    </p>
  ) : null;

  const filterBody =
    displayItemsState && displayItemsState.length > 0
      ? displayItemsState?.map(({ id, title, itemsCount, checked }) => (
          <FilterFormItemWrapper key={id} type="checkbox">
            <FormCheckWrapper isFilterRow>
              <Checkbox
                id={id}
                checked={checked}
                name={filterData.name}
                onChange={({ target: { checked: newCheckboxChecked } }) =>
                  handleCheckboxChange({ id, value: newCheckboxChecked })
                }
              />
              <FilterFormLabel
                name={title}
                total={itemsCount?.toString()}
                htmlFor={id}
              />
            </FormCheckWrapper>
          </FilterFormItemWrapper>
        ))
      : noResults;

  const onSearchClear = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef?.current]);

  const onToggleAccordionItem = useCallback(
    (id, isExpanded, event) => {
      onToggleItem?.(id, isExpanded, event);
    },
    [onToggleItem],
  );

  return (
    <AbstractFilter
      opened={opened}
      variant={theme}
      title={filterData.categoryName}
      subtitle={truncateString(selectedItemsTitles, 38)}
      badge={{
        count: totalItemsCount,
      }}
      clearAll={{
        clearAllText: labels?.clearAll,
        clearAllCallback: handleClear,
      }}
      classes={classes}
      onToggle={onToggleAccordionItem}
      {...abstractFilterProps}
    >
      {hasSearch && (
        <FilterFormItemWrapper type="search">
          <form className="search-container">
            <SearchInput
              id="filter-search-input"
              searchTerm={searchTermState}
              placeholderLabel={labels?.searchPlaceholder}
              autoComplete="off"
              onChange={handleOnSearch}
              ref={inputRef}
              onClear={onSearchClear}
            />
          </form>
        </FilterFormItemWrapper>
      )}
      {abstractFilterProps.filterType === "accordion" ? (
        filterBody
      ) : (
        <ScrollContainer>{filterBody}</ScrollContainer>
      )}
      {displayShowMoreState &&
        displayItemsState.length >= showMoreItemsLimit && (
          <ShowMoreLess
            isShown={showMoreExpanded}
            showLessLabel={labels?.showLess}
            showMoreLabel={labels?.showMore}
            onChange={setIsExpanded}
          />
        )}
    </AbstractFilter>
  );
};

export default CheckboxFilter;
